import { delay } from '@passionware/platform-js';
import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { v4 } from 'uuid';
import { AppScopeProvider } from 'v5/core/app-scope/_private/AppScopeProvider';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithContentThemeService } from 'v5/services/front/ThemeService/ThemeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import { I18nInstanceProvider } from 'v5/i18n';
import { ContentThemeScope } from './content-theme/contentTheme';

type RenderedElement = {
  node: ReactNode;
  id: string;
};

type MountElementHandlerProps = WithServices<
  [
    WithMessageService,
    WithFeatureService,
    WithAppScopeService,
    WithContentThemeService
  ]
>;
/**
 * Global gateway for mounting dialogs independently of their triggers.
 *
 * Primary use case: When a dialog's trigger (e.g., button in a dropdown or route) gets unmounted
 * while the dialog is closing, the dialog's fade-out animation would be abruptly cut off.
 * By mounting the dialog content here (at the app root), the exit animation completes gracefully
 * even after the trigger is gone.
 *
 * Features:
 * - Captures focused element at mount time and restores focus on close
 * - Manages open/close state independently of trigger lifecycle
 * - Cleans up after exit animation completes
 * - Re-establishes opener browsing scope via {@link AppScopeProvider}, and
 *   the opener's translations when it passes them (a public page)
 * - Renders in the content theme: the dialogs belong to the page content,
 *   wherever they were opened
 * - `close()` flushes open=false synchronously so children (e.g. unsaved-changes
 *   guards) update before any immediate follow-up such as in-app navigation
 */

export function MountElementHandler({ services }: MountElementHandlerProps) {
  const [elements, setElements] = useState<RenderedElement[]>([]);

  useEffect(() => {
    const unsubscribe = services.messageService.mountElement.subscribeToRequest(
      payload => {
        const id = v4();
        const { appScope, i18n } = payload.request;
        appScopeUtils.assertValid(appScope);
        // Capture the currently focused element before mounting the dialog
        const previouslyFocusedElement =
          document.activeElement as HTMLElement | null;

        const element = (
          <AppScopeProvider scope={appScope}>
            <ContentThemeScope services={services}>
              <ElementRenderer
                key={id}
                previouslyFocusedElement={previouslyFocusedElement}
                renderChildren={api => {
                  const scheduleUnmountAfterClose = () => {
                    payload.sendResponse(undefined);
                    void (async () => {
                      await delay(1000);
                      setElements(prev => prev.filter(e => e.id !== id));
                    })();
                  };

                  const handleOpenChange = (nextOpen: boolean) => {
                    if (nextOpen) {
                      api.onOpenChange(true);
                      return;
                    }
                    flushSync(() => api.onOpenChange(false));
                    scheduleUnmountAfterClose();
                  };

                  return payload.request.handler({
                    ...api,
                    onOpenChange: handleOpenChange,
                    close: () => handleOpenChange(false)
                  });
                }}
              />
            </ContentThemeScope>
          </AppScopeProvider>
        );
        const renderedElement = i18n ? (
          <I18nInstanceProvider i18n={i18n}>{element}</I18nInstanceProvider>
        ) : (
          element
        );
        setElements(prev => [...prev, { node: renderedElement, id }]);
      }
    );
    return () => unsubscribe();
  }, [services]);

  return elements.map(e => <Fragment key={e.id}>{e.node}</Fragment>);
}

function ElementRenderer({
  renderChildren,
  previouslyFocusedElement
}: {
  renderChildren: (api: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }) => ReactNode;
  previouslyFocusedElement: HTMLElement | null;
}) {
  const [open, setOpen] = useState(true);
  const hasRestoredFocus = useRef(false);

  useEffect(() => {
    // Restore focus when dialog closes
    if (!open && !hasRestoredFocus.current) {
      hasRestoredFocus.current = true;
      // Use requestAnimationFrame to ensure DOM is ready after dialog unmounts
      requestAnimationFrame(() => {
        if (
          previouslyFocusedElement &&
          document.body.contains(previouslyFocusedElement)
        ) {
          previouslyFocusedElement.focus();
        }
      });
    }
  }, [open, previouslyFocusedElement]);

  return renderChildren({ open, onOpenChange: setOpen });
}
